import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../../components/Button/Button'
import { useAppDispatch } from '../../../../store/hooks'
import { setCrossClientLandingDecompositionResult } from '../../../../store/crossClient/crossClientLanding'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { type CrossClientLandingPrefillState } from '../../Landing/hooks'
import { CcqCards, cardForResult, ccqOffersDeepAnalysis, type CcqCard } from './ccqResultView'
import { CcqClientRow, CcqClientRowSkeleton } from './CcqClientRow'
import { CcqStatusIcon } from './CcqStatusIcon'
import { CcqFullList } from './CcqFullList'
import { CcqAiFooter } from './CcqAiFooter'
import { CcqMatchedFooter } from './CcqMatchedFooter'
import {
  CCQ_CARD_DESCRIPTION,
  CCQ_CARD_EMPTY_DONE,
  CCQ_CARD_EMPTY_RUNNING,
  CCQ_CARD_LABEL,
  CCQ_CLOSE_FULL_LIST,
  CCQ_EDIT_AND_RUN_AGAIN,
  CCQ_LIST_LOADING,
  CCQ_NO_MATCHES_DONE,
  CCQ_PREVIEWS_FAILED,
  CCQ_TRY_AGAIN,
  ccqSeeAllLabel,
  formatCount,
} from './ccqRunCopy'
import { CcqAiBatchSteps, useCcqAi } from './useCcqAi'
import { CCQ_PREVIEW_SIZE } from './useCcqCardResults'
import { useCcqClientsReady } from './useCcqClientView'
import { useCcqRun, useCcqRunDisplay } from './useCcqRun'
import { useCcqCursor } from './useCcqListCursor'
import { CcqFullListTransitions, useCcqWorkspace } from './useCcqWorkspace'
import { CcqFillAnchors, useFillToViewportBottom } from './useFillToViewportBottom'
import styles from './CcqGroupCard.module.scss'
import { CCQ_BADGE_CLASS, CCQ_SECONDARY_BUTTON_CLASS } from './ccqClassNames'

// Matches .reveal and .fold in CcqGroupCard.module.scss.
const CARD_FOLD_MS = 240

interface CcqGroupCardProps {
  card: CcqCard
}

// The animated counts redraw every frame of a tween, so only these read them, not the whole card.
const CcqCardCount: React.FC<CcqGroupCardProps> = ({ card }) => <>{formatCount(useCcqRunDisplay().counts[card])}</>

const CcqSeeAllLabel: React.FC<CcqGroupCardProps> = ({ card }) => <>{ccqSeeAllLabel(card, useCcqRunDisplay().counts[card])}</>

export const CcqGroupCard: React.FC<CcqGroupCardProps> = ({ card }) => {
  const { hasCounts, isDone, counts, unlockedCards, execution } = useCcqRun()
  const { expanded, toggleCard, previews: cardPreviews, previewsLoaded, previewsFailed, retryPreviews, takeListCloseFocus, panelResult } = useCcqWorkspace()
  const cursor = useCcqCursor()
  const { card: fullListCard, swap: fullListTransition, open: openFullList, close: closeFullList } = cursor.list
  const seeAllRef = useRef<HTMLButtonElement>(null)
  const headButtonRef = useRef<HTMLButtonElement>(null)
  const { batchFor } = useCcqAi()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isMatched = card === CcqCards.MATCHED
  const isFullList = fullListCard === card
  // The badge animates between polls; what has actually landed decides the body.
  const landedCount = counts[card]
  const previews = cardPreviews[card]
  // Previews freeze while a panel is open, so a client can land without a row, and the panel's client can leave the card
  // (an AI check, say) while keeping theirs. See all shows while any landed client has no row of its own here.
  const hasLeftPanelRow = panelResult !== null && cardForResult(panelResult) !== card && previews.some((result) => result.id === panelResult.id)
  const hasMore = landedCount > previews.length - (hasLeftPanelRow ? 1 : 0)
  const arePreviewClientsReady = useCcqClientsReady(previews)
  const arePreviewsLoading = landedCount > 0 && (!previewsLoaded[card] || !arePreviewClientsReady)
  // An AI batch can move every client out of its card; the card stays open on its progress, Stop and summary.
  const hasAiBatch = ccqOffersDeepAnalysis(card) && batchFor(card).step !== CcqAiBatchSteps.IDLE
  const isEnabled = isFullList || unlockedCards[card] === true || hasAiBatch
  const showsEmptyMatched = isMatched && isDone && landedCount === 0
  const isExpanded = isFullList || ((isEnabled || showsEmptyMatched) && expanded[card])

  // A closing body stays mounted through its fold. Tracked during render (not in an effect), so the fold
  // starts on the same frame another card starts opening.
  const [wasExpanded, setWasExpanded] = useState(isExpanded)
  const [isCollapsing, setIsCollapsing] = useState(false)
  // Rows animate in only as the card opens, not when returning from the full list or remounting open.
  const [animatesContent, setAnimatesContent] = useState(false)
  const [wasFullList, setWasFullList] = useState(isFullList)
  if (wasExpanded !== isExpanded) {
    setWasExpanded(isExpanded)
    setIsCollapsing(!isExpanded)
    setAnimatesContent(isExpanded)
  }
  if (wasFullList !== isFullList) {
    setWasFullList(isFullList)
    setAnimatesContent(false)
  }

  useEffect(() => {
    if (!isCollapsing) {
      return
    }
    const timer = setTimeout(() => {
      setIsCollapsing(false)
    }, CARD_FOLD_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [isCollapsing])

  useEffect(() => {
    // A card whose rows caught up while the list was open has no See all; its header takes focus instead. An
    // empty card leaves it to the page title.
    const focusTarget = seeAllRef.current ?? (landedCount > 0 ? headButtonRef.current : null)
    if (!isFullList && focusTarget !== null && takeListCloseFocus(card)) {
      focusTarget.focus()
    }
  }, [isFullList, card, takeListCloseFocus, landedCount])

  const showsBody = isExpanded || isCollapsing
  const hidesMatchedBadge = isMatched && landedCount === 0 && !isDone
  const headingId = `ccq-card-${card}-heading`
  const bodyId = `ccq-card-${card}-body`

  const handleView = (result: CrossClientQueryResult) => {
    cursor.open(result.id, card)
  }

  const handleEditAndRunAgain = () => {
    dispatch(setCrossClientLandingDecompositionResult(null))
    const landingState: CrossClientLandingPrefillState = { prefillQuestion: execution.userQuestion }
    navigate('/cross-client', { state: landingState })
  }

  const previewRows = (
    <ul className={classNames({ [styles.contentIn]: animatesContent, 'pb-3': !hasMore }, 'list-unstyled d-flex flex-column gap-1 mb-0')}>
      {arePreviewsLoading
        ? Array.from({ length: Math.min(CCQ_PREVIEW_SIZE, landedCount) }, (_, index) => (
            <CcqClientRowSkeleton key={index} showsEmail={false} hasCheckbox={false} />
          ))
        : previews.map((result) => (
            <CcqClientRow
              key={result.id}
              result={result}
              card={card}
              isSelected={cursor.isSelected(result.id, card)}
              showsEmail={false}
              checkbox={null}
              onView={handleView}
            />
          ))}
      {hasMore ? (
        <li className='px-2'>
          <div className='zocks-py-0.75 zocks-ps-2.25'>
            <Button
              ref={seeAllRef}
              variant='primaryLinkButton'
              className={classNames(styles.seeAllLink, 'button-small text-decoration-none d-inline-flex align-items-center gap-1 p-0 border-0')}
              onClick={() => openFullList(card)}
            >
              <CcqSeeAllLabel card={card} />
              {/* Always rendered (shown on hover) so the label never shifts. */}
              <i className={classNames('bi bi-chevron-expand', styles.rowLinkIcon)} aria-hidden='true' />
            </Button>
          </div>
        </li>
      ) : null}
    </ul>
  )

  const message = (text: string, action: React.ReactNode = null) => (
    <div className={classNames({ [styles.contentIn]: animatesContent }, 'd-flex flex-column align-items-start gap-2 zocks-pl-2.75 pe-3 py-3')}>
      <span className='paragraph-small zocks-text-gray-700'>{text}</span>
      {action}
    </div>
  )

  const body = (() => {
    if (!hasCounts) {
      return null
    }
    if (isFullList) {
      return <CcqFullList card={card} isLeaving={fullListTransition?.kind === CcqFullListTransitions.CLOSING && fullListTransition.card === card} />
    }
    if (showsEmptyMatched) {
      return message(
        CCQ_NO_MATCHES_DONE,
        <Button variant='header' className={CCQ_SECONDARY_BUTTON_CLASS} onClick={handleEditAndRunAgain}>
          {CCQ_EDIT_AND_RUN_AGAIN}
        </Button>,
      )
    }
    if (landedCount === 0) {
      return message(isDone ? CCQ_CARD_EMPTY_DONE : CCQ_CARD_EMPTY_RUNNING)
    }
    if (previewsFailed[card]) {
      return message(
        CCQ_PREVIEWS_FAILED,
        <Button variant='primaryLinkButton' className='button-small text-decoration-none p-0 border-0' onClick={() => retryPreviews(card)}>
          {CCQ_TRY_AGAIN}
        </Button>,
      )
    }
    return (
      <>
        {arePreviewsLoading ? (
          <span className='visually-hidden' role='status'>
            {CCQ_LIST_LOADING}
          </span>
        ) : null}
        {previewRows}
      </>
    )
  })()

  const showsFooter = isMatched && hasCounts && landedCount > 0
  const showsAiFooter = ccqOffersDeepAnalysis(card) && hasCounts && (landedCount > 0 || hasAiBatch)

  // A full list pins its card open (its header holds the close button, whose click must not toggle).
  const isToggleable = isEnabled && !isFullList

  const cardRef = useFillToViewportBottom<HTMLElement>(isFullList, CcqFillAnchors.SELF)
  const fill = isFullList ? styles.fill : null

  const handleHeadClick = () => {
    if (!isToggleable) {
      return
    }
    toggleCard(card)
  }

  return (
    <section
      ref={cardRef}
      aria-labelledby={headingId}
      className={classNames(
        styles.card,
        'd-flex flex-column border rounded-4',
        isExpanded ? classNames(styles.cardOpen, 'zocks-border-color-gray-300') : 'zocks-border-color-gray-200',
        { [styles.cardHover]: isEnabled && !isExpanded },
        isEnabled ? 'zocks-bg-color-white' : 'zocks-bg-color-gray-100',
      )}
    >
      {/* The whole header toggles the card. The buttons inside are for keyboard and screen readers; their
          clicks bubble up to here, so neither toggles on its own. */}
      <div className={classNames(styles.head, 'd-flex align-items-center zocks-gap-0.75 p-3', { [styles.headToggle]: isToggleable })} onClick={handleHeadClick}>
        <Button
          ref={headButtonRef}
          type='button'
          data-ccq-card-head={isFullList ? undefined : card}
          className='d-flex align-items-center justify-content-start zocks-gap-0.75 flex-grow-1 mw-0 bg-transparent border-0 p-0 text-start'
          disabled={!isEnabled || isFullList}
          aria-expanded={isEnabled ? isExpanded : undefined}
          aria-controls={bodyId}
        >
          <CcqStatusIcon card={card} size='regular' />
          <span className='paragraph-small mw-0'>
            <h2 id={headingId} className='d-inline paragraph-small bold text-uppercase zocks-text-gray-900 text-nowrap me-2 mb-0'>
              {CCQ_CARD_LABEL[card]}
            </h2>
            <span className='zocks-text-gray-700'>{CCQ_CARD_DESCRIPTION[card]}</span>
          </span>
        </Button>
        <span className={classNames(styles.countSlot, 'd-flex justify-content-end flex-shrink-0')}>
          {hasCounts && !hidesMatchedBadge ? (
            <span className={classNames(styles.countIn, CCQ_BADGE_CLASS, 'zocks-text-gray-900')}>
              <CcqCardCount card={card} />
            </span>
          ) : null}
        </span>
        {/* Always rendered (hidden until the card unlocks) so it holds its place and can fade in. */}
        {isFullList ? (
          <Button
            type='button'
            className={classNames(styles.chevronButton, 'd-flex align-items-center justify-content-center flex-shrink-0 bg-transparent border-0 p-0')}
            onClick={closeFullList}
            aria-label={CCQ_CLOSE_FULL_LIST}
            // The header button is disabled while the list is open, so focus falls back here instead.
            data-ccq-card-head={card}
          >
            <i className='bi bi-x-lg d-block h-auto zocks-text-gray-900' aria-hidden='true' />
          </Button>
        ) : (
          <Button
            type='button'
            className={classNames(styles.chevronButton, 'd-flex align-items-center justify-content-center flex-shrink-0 bg-transparent border-0 p-0', {
              [styles.chevronHidden]: !isEnabled,
            })}
            disabled={!isEnabled}
            aria-hidden={!isEnabled}
            tabIndex={isEnabled ? undefined : -1}
            aria-label={isExpanded ? `Collapse ${CCQ_CARD_LABEL[card]}` : `Expand ${CCQ_CARD_LABEL[card]}`}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
          >
            <i
              className={classNames('bi bi-chevron-down d-block h-auto zocks-text-gray-900', styles.chevron, { [styles.chevronOpen]: isExpanded })}
              aria-hidden='true'
            />
          </Button>
        )}
      </div>
      {showsBody ? (
        <div className={classNames(isCollapsing ? styles.fold : styles.reveal, fill)} aria-hidden={isCollapsing ? true : undefined}>
          <div className={classNames(styles.revealClip, fill)}>
            <div id={bodyId} className={classNames('d-flex flex-column', fill)}>
              <div className={classNames('flex-grow-1', fill)}>{body}</div>
              {showsFooter ? <CcqMatchedFooter isSticky={isFullList} /> : null}
              {showsAiFooter ? <CcqAiFooter card={card} /> : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
